import React, { useEffect } from 'react'
import { Platform } from 'react-native'

import { bindHover, usePopupState } from 'material-ui-popup-state/hooks'
import { useIntl } from 'react-intl'

import { Theme, useMediaQuery } from '@mui/material'

import EyeSVG from 'app/assets/icons/eye.svg'
import WhiteEyeSVG from 'app/assets/icons/profile/auctions/white-eye.svg'
import Popper from 'app/components/common/Popover'
import useVerifyAuth from 'app/features/ListingFeature/hooks/useVerifyAuth'
import Alert from 'app/services/Alert'
import ErrorHandler, { isErrorHandlerType } from 'app/services/ErrorHandler'
import {
  useAddAuctionToWatchList,
  useDeleteAuctionFromWatchList
} from 'app/services/Query/auction/auction.mutation'

import { ButtonProps } from '../../base/Button'
import IconButton from '../../base/IconButton'

interface WatchButtonProps {
  isActive?: boolean
  sx?: ButtonProps['sx']
  auctionId: number
  watchListId?: string
}

const WatchButton: RFC<WatchButtonProps> = ({
  isActive,
  sx,
  auctionId,
  watchListId
}) => {
  const intl = useIntl()
  const checkAuth = useVerifyAuth()
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'))

  const {
    mutateAsync,
    isLoading: isLoadingAdd,
    isSuccess: isSuccessAdded,
    reset: resetAdd
  } = useAddAuctionToWatchList()
  const {
    mutateAsync: deleteMutation,
    isLoading: isLoadingDelete,
    isSuccess: isSuccessDeleted,
    reset: resetDelete
  } = useDeleteAuctionFromWatchList()

  const popupState = usePopupState({
    variant: 'popper',
    popupId: 'messagePopoverId'
  })

  const handleAddToWatchlist = async () => {
    try {
      if (auctionId && !isActive) {
        await mutateAsync(auctionId)
        Alert.success(
          intl.formatMessage({
            defaultMessage: 'Added to watchlist'
          })
        )
      }

      if (auctionId && isActive) {
        await deleteMutation(watchListId || '')

        Alert.success(
          intl.formatMessage({
            defaultMessage: 'Removed from Watchlist'
          })
        )
      }
    } catch (error) {
      if (isErrorHandlerType(error)) new ErrorHandler(error.data)
    }
  }

  const activeText = intl.formatMessage({
    defaultMessage: 'Remove from Watchlist'
  })
  const inActiveText = intl.formatMessage({
    defaultMessage: 'Add to Watchlist'
  })

  const text = isActive ? activeText : inActiveText
  const isDisabled =
    isLoadingAdd || isLoadingDelete || isSuccessAdded || isSuccessDeleted

  useEffect(() => {
    if (isActive && isSuccessAdded) {
      resetAdd()
    }

    if (!isActive && isSuccessDeleted) {
      resetDelete()
    }
  }, [isActive])

  return (
    <>
      <IconButton
        onClick={() => checkAuth(handleAddToWatchlist)}
        disabled={isDisabled}
        data-testid="watchlist-button"
        aria-label="Add to watchlist"
        sx={{
          minWidth: 48,
          width: 48,
          borderWidth: 1,
          borderStyle: 'solid',
          borderColor: 'stroke.main',
          height: 48,
          m: 0,
          borderRadius: '8px',
          ...{
            ...Platform.select({
              web: {
                background: isActive
                  ? 'linear-gradient(90deg, #17D3FF 0%, #00EA90 100%)'
                  : 'inherit'
              }
            })
          },
          ...sx
        }}
        {...Platform.select({
          web: bindHover(popupState)
        })}
      >
        {!isActive && <EyeSVG />}
        {isActive && <WhiteEyeSVG />}
      </IconButton>

      {popupState.isOpen && !isMobile && (
        <Popper text={text} popupState={popupState} />
      )}
    </>
  )
}

export default WatchButton
